/**
 * GapSidebar - Review queue for gaps/silences in context.
 * Duration is the key signal, displayed as a prominent badge.
 * Follows Libretto's calm, editorial design system.
 */
import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Typography, IconButton } from '@mui/material';
import {
    Close as CloseIcon,
    PlayArrow as PlayIcon,
} from '@mui/icons-material';
import { GapStore } from './GapStore';
import { GapOccurrence, formatTimestamp } from './gap';
import { CompositionService } from '../../../librettoEditor/services/CompositionService';
import { ScriptLayerStore } from '../../../librettoEditor/store/ScriptLayerStore';

interface GapSidebarProps {
    onClose: () => void;
}

const GapSidebar: React.FC<GapSidebarProps> = observer(({ onClose }) => {
    const gaps = GapStore.gaps;
    const currentIndex = GapStore.currentIndex;
    
    const handleItemClick = (index: number) => {
        GapStore.goToIndex(index);
        const gap = GapStore.gaps[index];
        if (gap) {
            // Map source time through segments to get correct composition time after cuts
            const compTime = ScriptLayerStore.mapSourceTimeToCompositionTime(gap.startSec);
            if (compTime !== null) {
                CompositionService.seek(compTime);
            } else {
                // Fallback if mapping fails
                CompositionService.seek(gap.startSec);
            }
        }
    };
    
    const handlePlayContext = async (gap: GapOccurrence) => {
        // Get context words with timestamps to determine playback range
        const context = GapStore.getContextWordsWithTimestamps(gap);
        
        let playStartSec: number;
        if (context && context.wordsBefore.length > 0) {
            // Start from the first context word before the gap
            playStartSec = context.wordsBefore[0].startSec;
        } else {
            // Just start from the gap itself with a small buffer
            playStartSec = Math.max(0, gap.startSec - 0.5);
        }
        
        // Map source time to composition time
        const compTime = ScriptLayerStore.mapSourceTimeToCompositionTime(playStartSec);
        const seekTime = compTime !== null ? compTime : playStartSec;
        
        // Seek and play
        await CompositionService.seek(seekTime);
        await CompositionService.play();
    };
    
    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'var(--app-bg-subtle)',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px 20px',
                    backgroundColor: 'var(--app-bg-surface)',
                    borderBottom: '1px solid var(--app-border-subtle)',
                }}
            >
                <Typography sx={{ 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: 'var(--app-text-primary)',
                    flex: 1,
                    letterSpacing: '-0.01em',
                }}>
                    Gaps
                </Typography>
                <Typography sx={{ 
                    fontSize: 12, 
                    color: 'var(--app-text-subtle)',
                    mr: 2,
                }}>
                    {gaps.length} found
                </Typography>
                <IconButton 
                    size="small" 
                    onClick={onClose}
                    sx={{ 
                        color: 'var(--app-text-subtle)',
                        '&:hover': { backgroundColor: 'var(--app-bg-subtle)', color: 'var(--app-text-primary)' },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>
            
            {/* Results list */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '12px',
                }}
            >
                {gaps.length === 0 ? (
                    <Box sx={{ 
                        padding: '40px 20px', 
                        textAlign: 'center',
                    }}>
                        <Typography sx={{ fontSize: 13, color: 'var(--app-text-subtle)' }}>
                            No gaps found exceeding {GapStore.minGapThreshold}s
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {gaps.map((gap, index) => (
                            <GapResultItem
                                key={gap.id}
                                gap={gap}
                                isSelected={index === currentIndex}
                                onClick={() => handleItemClick(index)}
                                onPlay={() => handlePlayContext(gap)}
                            />
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
});

interface GapResultItemProps {
    gap: GapOccurrence;
    isSelected: boolean;
    onClick: () => void;
    onPlay: () => void;
}

const GapResultItem: React.FC<GapResultItemProps> = ({
    gap,
    isSelected,
    onClick,
    onPlay,
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const displayStartSec = ScriptLayerStore.mapSourceTimeToCompositionTime(gap.startSec) ?? gap.startSec;
    
    // Build context string
    const contextBefore = gap.contextBefore.join(' ');
    const contextAfter = gap.contextAfter.join(' ');
    
    // Format duration nicely
    const formatDuration = (sec: number) => {
        if (sec >= 1) {
            return `${sec.toFixed(1)}s`;
        }
        return `${Math.round(sec * 1000)}ms`;
    };
    
    const handlePlayClick = (e: React.MouseEvent) => {
        e.stopPropagation(); // Don't trigger the item click
        onPlay();
    };
    
    return (
        <Box
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                padding: '12px 14px',
                cursor: 'pointer',
                borderRadius: '10px',
                backgroundColor: isSelected ? 'var(--app-bg-surface)' : 'transparent',
                border: isSelected ? '1px solid var(--app-border-subtle)' : '1px solid transparent',
                boxShadow: isSelected ? '0px 1px 3px rgba(0, 0, 0, 0.04)' : 'none',
                position: 'relative',
                transition: 'all 0.1s ease',
                '&:hover': {
                    backgroundColor: 'var(--app-bg-surface)',
                    border: '1px solid var(--app-border-subtle)',
                },
            }}
        >
            {/* Left accent bar for selected item */}
            {isSelected && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 3,
                        height: 24,
                        backgroundColor: '#2B6BFD',
                        borderRadius: '0 2px 2px 0',
                    }}
                />
            )}
            
            {/* Timestamp - left aligned for scannability */}
            <Typography
                sx={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: 'var(--app-text-muted)',
                    flexShrink: 0,
                    width: 48,
                    fontVariantNumeric: 'tabular-nums',
                }}
            >
                {formatTimestamp(displayStartSec)}
            </Typography>
            
            {/* Duration badge - prominent */}
            <Box
                sx={{
                    flexShrink: 0,
                    ml: 1.5,
                    mr: 1.5,
                    padding: '2px 8px',
                    borderRadius: '6px',
                    backgroundColor: '#FEF3C7',
                    border: '1px solid #FDE68A',
                }}
            >
                <Typography
                    sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: '#92400E',
                        fontVariantNumeric: 'tabular-nums',
                    }}
                >
                    {formatDuration(gap.durationSec)}
                </Typography>
            </Box>
            
            {/* Context - secondary */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontSize: 12,
                        lineHeight: 1.5,
                        color: 'var(--app-text-subtle)',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                    }}
                >
                    {contextBefore && (
                        <span>...{contextBefore}</span>
                    )}
                    {contextBefore && contextAfter && (
                        <span style={{ color: 'var(--app-border-muted)' }}> • </span>
                    )}
                    {contextAfter && (
                        <span>{contextAfter}...</span>
                    )}
                </Typography>
            </Box>
            
            {/* Play button - only on hover */}
            <Box
                sx={{
                    opacity: isHovered ? 1 : 0,
                    transition: 'opacity 0.1s ease',
                    ml: 1,
                    flexShrink: 0,
                }}
            >
                <IconButton
                    size="small"
                    onClick={handlePlayClick}
                    sx={{
                        padding: '4px',
                        color: 'var(--app-text-muted)',
                        '&:hover': {
                            color: '#2B6BFD',
                            backgroundColor: 'rgba(43, 107, 253, 0.08)',
                        },
                    }}
                >
                    <PlayIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>
        </Box>
    );
};

export default GapSidebar;
